<template>
  <div class="chart-box">
    <svg :viewBox="`0 0 ${W} ${H}`" class="chart" @mousemove="onMove" @mouseleave="hover = null">
      <defs>
        <pattern :id="hatchId" width="7" height="7" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
          <line x1="0" y1="0" x2="0" y2="7" class="hatch-line" />
        </pattern>
      </defs>
      <rect :x="PAD.l" :y="PAD.t" :width="W - PAD.l - PAD.r" :height="H - PAD.t - PAD.b" :fill="`url(#${hatchId})`" />
      <g v-for="t in yTicks" :key="`g${t}`">
        <line :x1="PAD.l" :x2="W - PAD.r" :y1="y(t)" :y2="y(t)" class="grid" />
        <text :x="PAD.l - 8" :y="y(t) + 3.5" class="tick" text-anchor="end">{{ t / 1e6 }}M</text>
      </g>
      <path :d="gapArea" class="gap-fill" />
      <line :x1="x(2027)" :x2="x(2027)" :y1="PAD.t" :y2="H - PAD.b" class="decision-line" />
      <path :d="incomePath" class="income" fill="none" />
      <path :d="expensePath" class="expense" fill="none" />
      <path :d="staffingPath" class="staffing" fill="none" />
      <!-- bold direct labels near 2027 -->
      <text :x="x(2027) + 8" :y="y(d27.totalIncome) - 8" class="lab lab-income">Total income {{ fmtM(d27.totalIncome) }}</text>
      <text :x="x(2027) + 8" :y="y(d27.totalExpenses) + 16" class="lab lab-expense">Total expenses {{ fmtM(d27.totalExpenses) }}</text>
      <text :x="x(2027) + 8" :y="y(d27.staffing) - 6" class="lab lab-staffing">of which staffing {{ fmtM(d27.staffing) }}</text>
      <line :x1="PAD.l" :x2="W - PAD.r" :y1="H - PAD.b" :y2="H - PAD.b" class="axis" />
      <text v-for="r in rows" :key="`x${r.year}`" :x="x(r.year)" :y="H - PAD.b + 16" class="tick" :class="{ 'tick-strong': r.year === 2027 }" text-anchor="middle">
        {{ r.year }}
      </text>
      <g v-if="hover">
        <line :x1="x(hover.year)" :x2="x(hover.year)" :y1="PAD.t" :y2="H - PAD.b" class="crosshair" />
        <circle :cx="x(hover.year)" :cy="y(hover.totalIncome)" r="4" class="dot income-dot" />
        <circle :cx="x(hover.year)" :cy="y(hover.totalExpenses)" r="4" class="dot expense-dot" />
      </g>
    </svg>
    <div class="legend">
      <span><i class="key key-income"></i>Total income</span>
      <span><i class="key key-expense"></i>Total expenses</span>
      <span><i class="key key-staffing"></i>Staffing</span>
    </div>
    <div v-if="hover" class="tooltip" :style="tooltipStyle">
      <div class="tt-title">{{ hover.year }}</div>
      <div>Income {{ fmtM(hover.totalIncome) }}</div>
      <div>Expenses {{ fmtM(hover.totalExpenses) }} (staffing {{ fmtM(hover.staffing) }})</div>
      <div class="tt-muted">Result {{ fmtM(hover.operatingResult) }}</div>
    </div>
  </div>
</template>

<script>
import { scaleLinear, linePath, areaBetween, niceTicks } from '../../utils/chartUtils';
import { formatM } from '../../utils/boardEngine';

let uid = 0;

export default {
  name: 'IncomeExpenseChart',
  props: { rows: { type: Array, required: true } },
  data: () => ({ W: 1000, H: 300, PAD: { l: 56, r: 24, t: 16, b: 30 }, hover: null, hatchId: `hatch-ie-${uid++}` }),
  computed: {
    x() {
      const years = this.rows.map((r) => r.year);
      return scaleLinear([Math.min(...years), Math.max(...years)], [this.PAD.l + 14, this.W - this.PAD.r - 210]);
    },
    yDomain() {
      const vals = this.rows.flatMap((r) => [r.totalIncome, r.totalExpenses, r.staffing]);
      const max = Math.max(...vals);
      return [0, max * 1.12];
    },
    y() { return scaleLinear(this.yDomain, [this.H - this.PAD.b, this.PAD.t]); },
    yTicks() { return niceTicks(0, this.yDomain[1], 4).filter((t) => t > 0); },
    d27() { return this.rows.find((r) => r.year === 2027) || this.rows[this.rows.length - 1]; },
    incomePath() { return linePath(this.rows.map((r) => [this.x(r.year), this.y(r.totalIncome)])); },
    expensePath() { return linePath(this.rows.map((r) => [this.x(r.year), this.y(r.totalExpenses)])); },
    staffingPath() { return linePath(this.rows.map((r) => [this.x(r.year), this.y(r.staffing)])); },
    gapArea() {
      return areaBetween(
        this.rows.map((r) => [this.x(r.year), this.y(r.totalIncome)]),
        this.rows.map((r) => [this.x(r.year), this.y(r.totalExpenses)]),
      );
    },
    tooltipStyle() {
      const px = (this.x(this.hover.year) / this.W) * 100;
      return { left: `min(max(${px}%, 90px), calc(100% - 170px))`, top: '4px' };
    },
  },
  methods: {
    fmtM: formatM,
    onMove(e) {
      const rect = e.currentTarget.getBoundingClientRect();
      const vx = ((e.clientX - rect.left) / rect.width) * this.W;
      let best = null, bd = Infinity;
      for (const r of this.rows) {
        const d = Math.abs(this.x(r.year) - vx);
        if (d < bd) { bd = d; best = r; }
      }
      this.hover = best;
    },
  },
};
</script>

<style scoped>
.chart-box { position: relative; }
.chart { width: 100%; height: auto; display: block; }
.hatch-line { stroke: var(--hatch); stroke-width: 1; }
.grid { stroke: var(--hairline); stroke-width: 1; }
.axis { stroke: var(--ink); stroke-width: 1.5; }
.tick { font-size: 11px; fill: var(--muted); font-variant-numeric: tabular-nums; }
.tick-strong { fill: var(--ink); font-weight: 700; }
.income { stroke: var(--ink); stroke-width: 2; stroke-linejoin: round; }
.expense { stroke: var(--red); stroke-width: 2; stroke-linejoin: round; }
.staffing { stroke: var(--red); stroke-width: 1.3; stroke-dasharray: 2 3; }
.gap-fill { fill: var(--ink); opacity: 0.06; }
.lab { font-size: 11.5px; font-weight: 700; }
.lab-income { fill: var(--ink); }
.lab-expense { fill: var(--red); }
.lab-staffing { fill: var(--muted); font-weight: 400; }
.decision-line { stroke: var(--ink); stroke-width: 1; stroke-dasharray: 3 4; }
.crosshair { stroke: var(--muted); stroke-width: 1; }
.dot { stroke: var(--paper); stroke-width: 2; }
.income-dot { fill: var(--ink); }
.expense-dot { fill: var(--red); }
.legend { display: flex; gap: 18px; font-size: 11px; color: var(--muted); margin-top: 6px; flex-wrap: wrap; }
.key { display: inline-block; width: 14px; height: 2px; vertical-align: middle; margin-right: 6px; }
.key-income { background: var(--ink); }
.key-expense { background: var(--red); }
.key-staffing { background: var(--red); height: 1px; border-bottom: 1px dotted var(--red); background: none; }
.tooltip {
  position: absolute; transform: translateX(-50%);
  background: var(--paper); border: 1px solid var(--ink); padding: 6px 10px;
  font-size: 12px; color: var(--ink); pointer-events: none; white-space: nowrap;
}
.tt-title { font-weight: 700; }
.tt-muted { color: var(--muted); font-size: 11px; }
</style>
